import { Card, Descriptions, Space, Tag, Tooltip } from 'antd';
import type { TenantOrder } from '@/mocks/orders';
import { formatVND } from '@/utils/format';
import {
  resolveShippingFeeDisplay,
  SHIPPING_FEE_ESTIMATE_HINT,
  SHIPPING_FEE_ESTIMATE_LABEL,
} from '@/pages/Orders/utils/shippingFeeDisplay';

interface OrderSummaryShippingExtraProps {
  order: TenantOrder;
}

const FEE_PAYER_LABEL: Record<string, string> = {
  seller: 'Người bán',
  buyer: 'Người mua',
};

/**
 * Hai khối "Vận chuyển" và "Thông tin kiện hàng" nằm ngay dưới "Thông tin nhận hàng" ở cột phải
 * pop-up xem nhanh (TCR-1095, góp ý tester 13/08 — trước đó cột phải chỉ có nhận hàng + ghi chú).
 *
 * Các dòng không có dữ liệu vẫn hiện "---" thay vì bị ẩn: tester đọc khối này để đối chiếu với
 * salework, khối co giãn theo dữ liệu làm hai bên khó so hàng nào với hàng nào.
 */
export function OrderSummaryShippingExtra({ order }: OrderSummaryShippingExtraProps) {
  const shipping = order.shipping;
  // Đơn bán tại quầy không có `shipping` — không có gì để nói về vận chuyển hay kiện hàng.
  if (!shipping) return null;

  const partner = shipping.shipping_partner;
  const pkg = shipping.package;
  const trackingNumber = partner?.order_id || shipping.tracking_number;
  const labelStyle = { color: '#666', width: 140 };

  /**
   * TCR-2605: hãng chưa báo phí thật thì dòng "Phí VC" phải hiện báo giá của hãng
   * kèm nhãn "(ước tính)" — không phải `0 ₫` (người dùng tưởng đơn miễn phí vận
   * chuyển trong khi hãng đã báo một khoản phí).
   *
   * Pop-up xem nhanh dùng `<ShippingCard hideCarrierInfo />` nên khối "Thông tin
   * giao vận" (chỗ `ShippingCard` in dòng phí ước tính) bị ẩn — dòng "Phí VC"
   * ở đây là chỗ DUY NHẤT còn lại, nên nó phải tự lo phần phí ước tính.
   *
   * Cùng luật với `ShippingCard`: đã có phí chốt → hiện số đó, KHÔNG gắn nhãn.
   */
  const shippingFeeDisplay = resolveShippingFeeDisplay({
    actual: shipping.shipping_cost,
    estimate: order.shipping_fee_estimate,
  });

  const formatDimension = (value: number | undefined, unit: string) =>
    typeof value === 'number' ? `${value} ${unit}` : '---';

  return (
    // Space riêng chứ không để trần trong Space của cột: antd gom cả Fragment vào MỘT ô, hai
    // card sẽ dính sát nhau trong khi các card khác của cột đều cách nhau.
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Card className="order-detail-card" size="small" title="Vận chuyển" data-testid="order-summary-shipping">
        <Descriptions column={1} size="small" bordered={false} labelStyle={labelStyle}>
          <Descriptions.Item label="Đơn vị VC">
            {partner?.name ? <Tag color="blue">{partner.name}</Tag> : '---'}
          </Descriptions.Item>
          <Descriptions.Item label="Bên trả phí">
            {shipping.fee_payer ? FEE_PAYER_LABEL[shipping.fee_payer] ?? shipping.fee_payer : '---'}
          </Descriptions.Item>
          <Descriptions.Item label="Phí VC">
            {shippingFeeDisplay.amount != null ? (
              <Space size={8} wrap>
                <span>{formatVND(shippingFeeDisplay.amount)}</span>
                {shippingFeeDisplay.isEstimate && (
                  <Tooltip title={SHIPPING_FEE_ESTIMATE_HINT}>
                    <Tag color="orange" style={{ marginInlineEnd: 0 }}>
                      {SHIPPING_FEE_ESTIMATE_LABEL}
                    </Tag>
                  </Tooltip>
                )}
              </Space>
            ) : (
              '---'
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Phí VC thực tế">
            {typeof order.shipping_fee_actual === 'number' ? formatVND(order.shipping_fee_actual) : '---'}
          </Descriptions.Item>
          <Descriptions.Item label="Mã vận đơn">{trackingNumber || '---'}</Descriptions.Item>
          <Descriptions.Item label="Ghi chú vận chuyển">{order.shipping_note || '---'}</Descriptions.Item>
        </Descriptions>
      </Card>

      <Card className="order-detail-card" size="small" title="Thông tin kiện hàng" data-testid="order-summary-package">
        <Descriptions column={1} size="small" bordered={false} labelStyle={labelStyle}>
          <Descriptions.Item label="Chiều dài">{formatDimension(pkg?.length, 'cm')}</Descriptions.Item>
          <Descriptions.Item label="Chiều rộng">{formatDimension(pkg?.width, 'cm')}</Descriptions.Item>
          <Descriptions.Item label="Chiều cao">{formatDimension(pkg?.height, 'cm')}</Descriptions.Item>
          <Descriptions.Item label="Cân nặng">{formatDimension(pkg?.weight, 'kg')}</Descriptions.Item>
        </Descriptions>
      </Card>
    </Space>
  );
}

export default OrderSummaryShippingExtra;
